/**
 * Plan entitlements and public pricing for AgilOdonto.
 * Source of truth for LandingPage, checkout and future enforcement.
 */

export const BILLING_CYCLE = {
  MONTHLY: 'monthly',
  ANNUAL: 'annual'
};

/** Extra clinic beyond included quota (BRL / month). */
export const EXTRA_CLINIC_MONTHLY_BRL = 49;

/** Max single attachment size before compression (bytes). */
export const MAX_ATTACHMENT_BYTES = 15 * 1024 * 1024;

/** Allowed MIME types for appointment media. */
export const ATTACHMENT_ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
  'application/pdf',
  'video/mp4',
  'video/quicktime'
];

/**
 * @typedef {Object} MediaCompression
 * @property {number} weightReductionMin - fraction of size/quality to remove (0–1)
 * @property {number} weightReductionMax
 * @property {number} jpegQuality - canvas export quality 0–1
 * @property {number} maxDimension - longest image edge in px
 * @property {number} videoScale - output scale vs original (0–1)
 * @property {number} videoBitrate - target videoBitsPerSecond
 */
/**
 * @typedef {Object} PlanEntitlements
 * @property {number|null} maxClinics
 * @property {number|null} maxProfessionals
 * @property {number|null} maxSecretaries - null = unlimited * @property {boolean} odontogram
 * @property {boolean} hofMap
 * @property {boolean} suggestions
 * @property {string[]} patientTabs
 * @property {boolean} availabilityByProfessional
 * @property {boolean} conflictBlocking
 * @property {boolean} pendingConfirmationsPanel
 * @property {boolean} clinicSettings
 * @property {boolean} reminderQueue
 * @property {boolean} appointmentMedia
 * @property {number} [mediaStorageMb] - total storage in megabytes when the plan is below 1 GB
 * @property {number} [mediaStorageMbPerClinic]
 * @property {number} [mediaStorageGb] - total storage included in the plan
 * @property {number} [mediaStorageGbPerClinic] - soft quota per clinic unit
 * @property {MediaCompression} mediaCompression
 * @property {boolean} multiClinicSwitcher
 * @property {boolean} advancedRoles
 * @property {boolean} assistedOnboarding
 * @property {boolean} roadmapPriority
 * @property {'email'|'priority'} support
 */

export const FREE_PLAN_ID = 'free';

/** Patient chart tabs available on the permanent Free plan. */
export const FREE_PATIENT_TABS = ['visao-geral', 'midias'];

/** Patient chart tabs on paid plans and during the 20-day trial. */
export const PAID_PATIENT_TABS = ['visao-geral', 'odontograma', 'hof', 'ortodontia', 'midias'];

const LIGHT_COMPRESSION = {
  weightReductionMin: 0.5,
  weightReductionMax: 0.7,
  jpegQuality: 0.4,
  maxDimension: 1280,
  videoScale: 0.5,
  videoBitrate: 600_000
};

/**
 * Permanent post-trial entry. Not billed (Stripe/OpenPix stay on the four paid plans).
 * @type {PricingPlan}
 */
export const FREE_PLAN = {
  id: FREE_PLAN_ID,
  name: 'Free',
  description: 'Acesso básico permanente após o trial de 20 dias',
  monthlyPriceBrl: 0,
  annualMonthlyPriceBrl: 0,
  monthlyPriceUsd: 0,
  annualMonthlyPriceUsd: 0,
  popular: false,
  billable: false,
  marketingFeatures: [
    '1 clínica',
    '1 profissional',
    'Agenda e calendário',
    'Paciente: visão geral + mídias',
    'Odontograma, HOF, Ortodontia e Sugestões com cadeado',
    '100 MB de espaço'
  ],
  entitlements: {
    maxClinics: 1,
    maxProfessionals: 1,
    maxSecretaries: 0,
    odontogram: false,
    hofMap: false,
    suggestions: false,
    patientTabs: FREE_PATIENT_TABS,
    availabilityByProfessional: true,
    conflictBlocking: true,
    pendingConfirmationsPanel: true,
    clinicSettings: true,
    reminderQueue: true,
    appointmentMedia: true,
    mediaStorageMb: 100,
    mediaStorageMbPerClinic: 100,
    mediaCompression: LIGHT_COMPRESSION,
    multiClinicSwitcher: false,
    advancedRoles: false,
    assistedOnboarding: false,
    roadmapPriority: false,
    support: 'email'
  }
};

/**
 * @typedef {Object} PricingPlan
 * @property {string} id
 * @property {string} name
 * @property {string} description
 * @property {number} monthlyPriceBrl
 * @property {number} annualMonthlyPriceBrl
 * @property {number} monthlyPriceUsd
 * @property {number} annualMonthlyPriceUsd
 * @property {boolean} popular
 * @property {string[]} marketingFeatures
 * @property {PlanEntitlements} entitlements
 */

/** @type {PricingPlan[]} */
export const PRICING_PLANS = [
  {
    id: 'individual',
    name: 'Individual',
    description: 'Para o dentista que atende sozinho',
    monthlyPriceBrl: 15,
    annualMonthlyPriceBrl: 12,
    monthlyPriceUsd: 15,
    annualMonthlyPriceUsd: 12,
    popular: false,
    marketingFeatures: [
      '1 clínica',
      '1 profissional',
      '500 MB de espaço',
      'Agenda, confirmação por link e lembrete por e-mail',
      'Odontograma + mapa HOF',
      'Anexos de mídia',
      'Sugestões da plataforma',
      'Suporte por e-mail'
    ],
    entitlements: {
      maxClinics: 1,
      maxProfessionals: 1,
      maxSecretaries: 0,
      odontogram: true,
      hofMap: true,
      suggestions: true,
      patientTabs: PAID_PATIENT_TABS,
      availabilityByProfessional: true,
      conflictBlocking: true,
      pendingConfirmationsPanel: true,
      clinicSettings: true,
      reminderQueue: true,
      appointmentMedia: true,
      mediaStorageMb: 500,
      mediaStorageMbPerClinic: 500,
      mediaCompression: {
        weightReductionMin: 0.5,
        weightReductionMax: 0.7,
        jpegQuality: 0.4,
        maxDimension: 1280,
        videoScale: 0.5,
        videoBitrate: 600_000
      },
      multiClinicSwitcher: false,
      advancedRoles: false,
      assistedOnboarding: false,
      roadmapPriority: false,
      support: 'email'
    }
  },
  {
    id: 'essencial',
    name: 'Essencial',
    description: 'Para consultórios começando a digitalizar',
    monthlyPriceBrl: 25,
    annualMonthlyPriceBrl: 20,
    monthlyPriceUsd: 25,
    annualMonthlyPriceUsd: 20,
    popular: false,
    marketingFeatures: [
      '1 clínica',
      'Até 2 secretárias',
      'Até 5 profissionais',
      'Agenda, confirmação por link e lembrete por e-mail',
      'Odontograma + mapa HOF',
      'Anexos de mídia (2 GB)',
      'Compressão de imagem/vídeo 50–70%',      'Suporte por e-mail'
    ],
    entitlements: {
      maxClinics: 1,
      maxProfessionals: 5,
      maxSecretaries: 2,
      odontogram: true,
      hofMap: true,
      suggestions: true,
      patientTabs: PAID_PATIENT_TABS,
      availabilityByProfessional: true,
      conflictBlocking: true,
      pendingConfirmationsPanel: true,
      clinicSettings: true,
      reminderQueue: true,
      appointmentMedia: true,
      mediaStorageGb: 2,
      mediaStorageGbPerClinic: 2,
      mediaCompression: {
        weightReductionMin: 0.5,
        weightReductionMax: 0.7,
        jpegQuality: 0.4,
        maxDimension: 1280,
        videoScale: 0.5,
        videoBitrate: 600_000
      },      multiClinicSwitcher: false,
      advancedRoles: false,
      assistedOnboarding: false,
      roadmapPriority: false,
      support: 'email'
    }
  },
  {
    id: 'clinica',
    name: 'Clínica',
    description: 'Para equipes com uma ou duas unidades',
    monthlyPriceBrl: 100,
    annualMonthlyPriceBrl: 80,
    monthlyPriceUsd: 100,
    annualMonthlyPriceUsd: 80,
    popular: true,
    marketingFeatures: [
      'Até 2 clínicas',
      'Até 6 secretárias',
      'Até 20 profissionais',
      'Tudo do Essencial',
      'Anexos de mídia (6 GB · 3 GB/clínica)',
      'Compressão de imagem/vídeo 30–40%',      'Suporte prioritário'
    ],
    entitlements: {
      maxClinics: 2,
      maxProfessionals: 20,
      maxSecretaries: 6,
      odontogram: true,
      hofMap: true,
      suggestions: true,
      patientTabs: PAID_PATIENT_TABS,
      availabilityByProfessional: true,
      conflictBlocking: true,
      pendingConfirmationsPanel: true,
      clinicSettings: true,
      reminderQueue: true,
      appointmentMedia: true,
      mediaStorageGb: 6,
      mediaStorageGbPerClinic: 3,
      mediaCompression: {
        weightReductionMin: 0.3,
        weightReductionMax: 0.4,
        jpegQuality: 0.65,
        maxDimension: 1920,
        videoScale: 0.7,
        videoBitrate: 1_200_000
      },      multiClinicSwitcher: true,
      advancedRoles: false,
      assistedOnboarding: false,
      roadmapPriority: false,
      support: 'priority'
    }
  },
  {
    id: 'rede',
    name: 'Rede',
    description: 'Para grupos e múltiplas unidades',
    monthlyPriceBrl: 300,
    annualMonthlyPriceBrl: 240,
    monthlyPriceUsd: 300,
    annualMonthlyPriceUsd: 240,
    popular: false,
    marketingFeatures: [
      'Até 10 clínicas',
      'Secretárias ilimitadas',
      'Profissionais ilimitados',
      'Switcher multi-clínica + onboarding',
      'Anexos de mídia (50 GB · 5 GB/clínica)',
      'Compressão de imagem/vídeo 10–20%',      'Prioridade de roadmap'
    ],
    entitlements: {
      maxClinics: 10,
      maxProfessionals: null,
      maxSecretaries: null,
      odontogram: true,
      hofMap: true,
      suggestions: true,
      patientTabs: PAID_PATIENT_TABS,
      availabilityByProfessional: true,
      conflictBlocking: true,
      pendingConfirmationsPanel: true,
      clinicSettings: true,
      reminderQueue: true,
      appointmentMedia: true,
      mediaStorageGb: 50,
      mediaStorageGbPerClinic: 5,
      mediaCompression: {
        weightReductionMin: 0.1,
        weightReductionMax: 0.2,
        jpegQuality: 0.85,
        maxDimension: 2560,
        videoScale: 0.9,
        videoBitrate: 2_500_000
      },      multiClinicSwitcher: true,
      advancedRoles: true,
      assistedOnboarding: true,
      roadmapPriority: true,
      support: 'priority'
    }
  }
];

export const WHATSAPP_ADDON = {
  id: 'whatsapp_auto',
  name: 'WhatsApp automático',
  monthlyPriceBrl: 39,
  notes:
    'Ativar quando Cloud API estiver conectada. Consumo Meta (~R$0,10–0,15/msg) à parte ou via créditos inclusos.'
};

/** Paid catalog shown on landing/checkout. Stripe/OpenPix stay on these four ids. */
export const PAID_PLAN_IDS = PRICING_PLANS.map((plan) => plan.id);

/** Full catalog including the post-trial Free plan. */
export const ALL_PLANS = [FREE_PLAN, ...PRICING_PLANS];

export const PLAN_IDS = ALL_PLANS.map((plan) => plan.id);

/**
 * @param {string} planId
 * @returns {boolean}
 */
export function isPaidPlan(planId) {
  return PAID_PLAN_IDS.includes(planId);
}

/**
 * @param {string} planId
 * @returns {PricingPlan|undefined}
 */
export function getPlanById(planId) {
  return ALL_PLANS.find((plan) => plan.id === planId);
}

/**
 * Catalog lookup of the plan stored on the clinic.
 * Missing planId falls back to Free so an unpaid clinic never inherits Clínica quotas.
 * Trial-full vs Free-limited is resolved by entitlementPlan.
 * @param {{ planId?: string }|null|undefined} clinic
 * @returns {PricingPlan}
 */
export function resolveClinicPlan(clinic) {
  return getPlanById(clinic?.planId) || FREE_PLAN;
}

/**
 * Per-clinic storage quota in bytes.
 * @param {PricingPlan} plan
 * @returns {number}
 */
export function getMediaStorageBytes(plan) {
  const mb =
    plan?.entitlements?.mediaStorageMbPerClinic ??
    plan?.entitlements?.mediaStorageMb;
  if (typeof mb === 'number') return mb * 1024 * 1024;
  const gb =
    plan?.entitlements?.mediaStorageGbPerClinic ??
    plan?.entitlements?.mediaStorageGb ??
    2;
  return gb * 1024 * 1024 * 1024;
}

/**
 * Human label for the per-clinic storage quota.
 * @param {PricingPlan} plan
 * @returns {string}
 */
export function formatStorageQuota(plan) {
  const mb =
    plan?.entitlements?.mediaStorageMbPerClinic ??
    plan?.entitlements?.mediaStorageMb;
  if (typeof mb === 'number') return `${mb} MB`;
  const gb =
    plan?.entitlements?.mediaStorageGbPerClinic ??
    plan?.entitlements?.mediaStorageGb ??
    2;
  return `${gb} GB`;
}

/**
 * Human label for compression range on a plan.
 * @param {PricingPlan} plan
 * @returns {string}
 */
export function getCompressionLabel(plan) {
  const c = plan?.entitlements?.mediaCompression;
  if (!c) return '';
  const min = Math.round(c.weightReductionMin * 100);
  const max = Math.round(c.weightReductionMax * 100);
  return `${min}–${max}%`;
}

/**
 * @param {PricingPlan} plan
 * @param {'monthly'|'annual'} cycle
 * @returns {number}
 */
export function currencyForLocale(locale) {
  return locale === 'pt-BR' ? 'brl' : 'usd';
}

/**
 * Moeda cobrada da clínica. Brasil paga em real; os demais países, em dólar.
 * Clínica antiga, sem country, continua pela regra do idioma.
 */
export function currencyForClinic(clinic, locale) {
  const country = typeof clinic?.country === 'string' ? clinic.country.trim().toUpperCase() : '';
  if (country === 'BR') return 'brl';
  if (/^[A-Z]{2}$/.test(country)) return 'usd';
  return currencyForLocale(locale);
}

export function getDisplayMonthlyPrice(plan, cycle, currency = 'brl') {
  if (!plan) return 0;
  if (currency === 'usd') {
    return cycle === BILLING_CYCLE.ANNUAL
      ? plan.annualMonthlyPriceUsd
      : plan.monthlyPriceUsd;
  }
  return cycle === BILLING_CYCLE.ANNUAL
    ? plan.annualMonthlyPriceBrl
    : plan.monthlyPriceBrl;
}

/**
 * Amount charged now for a billing period (annual is prepaid).
 * @param {PricingPlan} plan
 * @param {'monthly'|'annual'} cycle
 * @returns {number}
 */
export function getPeriodPrice(plan, cycle, currency = 'brl') {
  const monthly = getDisplayMonthlyPrice(plan, cycle, currency);
  return cycle === BILLING_CYCLE.ANNUAL ? monthly * 12 : monthly;
}

export function getPeriodPriceBrl(plan, cycle) {
  return getPeriodPrice(plan, cycle, 'brl');
}

/**
 * @param {PricingPlan} plan
 * @param {'monthly'|'annual'} cycle
 * @returns {number}
 */
export function getPeriodPriceCents(plan, cycle) {
  return getPeriodPriceBrl(plan, cycle) * 100;
}

/**
 * @param {number} value
 * @returns {string}
 */
export function formatPlanPrice(value, currency = 'brl') {
  return currency === 'usd' ? `US$ ${value}` : `R$ ${value}`;
}

/**
 * @param {'monthly'|'annual'|string|null|undefined} cycle
 * @returns {'monthly'|'annual'}
 */
export function normalizeBillingCycle(cycle) {
  return cycle === BILLING_CYCLE.ANNUAL ? BILLING_CYCLE.ANNUAL : BILLING_CYCLE.MONTHLY;
}

/**
 * @param {number|null} limit
 * @param {number} current
 * @returns {boolean}
 */
export function isWithinLimit(limit, current) {
  if (limit == null) return true;
  return current <= limit;
}
